import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Loader2,
  MessageCircleQuestion,
  Moon,
  Store,
  Sun,
} from 'lucide-react';
import { FileUpload, createUploadFile, formatInstant } from '@ampersandbrands/ui';
import {
  useStoreMe,
  useEffectiveStoreNo,
  isNotAvailable,
  type StoreChecklistItem,
} from '@/lib/useStores';
import {
  useDailyReport,
  useSubmitReport,
  useUploadReportPhoto,
  todayInStoreZone,
  type Phase,
  type PhaseReport,
  type ReportChecklistEntry,
} from '@/lib/useToday';

// --- Shared UI atoms (unchanged from the previous design) ---

function CardShell({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      {children}
    </div>
  );
}

function CardHeader({
  icon,
  title,
  submittedAt,
}: {
  icon: ReactNode;
  title: string;
  submittedAt?: string | null;
}) {
  return (
    <div className="flex items-center justify-between border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark">
          {icon}
        </span>
        <span className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">
          {title}
        </span>
      </div>
      {submittedAt && (
        <span className="flex items-center gap-1 rounded-pill bg-candy-teal/10 px-2.5 py-1 text-[12px] font-medium text-candy-teal dark:text-candy-teal-dark">
          <CheckCircle2 size={12} aria-hidden />
          {formatInstant(submittedAt)}
        </span>
      )}
    </div>
  );
}

function InfoBody({ children }: { children: ReactNode }) {
  return (
    <div className="px-4 py-4 text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
      {children}
    </div>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1 block text-[13px] font-medium text-candy-ink-muted dark:text-candy-dark-ink-muted"
    >
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-md border border-candy-line bg-candy-white px-3 py-2 text-[15px] ' +
  'text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-pink focus:outline-none ' +
  'dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink ' +
  'dark:placeholder:text-candy-dark-ink-muted dark:focus:border-candy-pink-dark';

function SubmitRow({
  pending,
  disabled,
  label,
  error,
}: {
  pending: boolean;
  disabled?: boolean;
  label: string;
  error?: string;
}) {
  return (
    <div className="border-t border-candy-line px-4 py-3 dark:border-candy-dark-line">
      {error && <p className="mb-2 text-[12px] text-red-500 dark:text-red-400">{error}</p>}
      <button
        type="submit"
        disabled={pending || disabled}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-candy-pink px-4 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
      >
        {pending && <Loader2 size={15} className="animate-spin" aria-hidden />}
        {pending ? 'Submitting…' : label}
      </button>
    </div>
  );
}

// --- Reusable checklist state hook ---

function useChecklistState(items: readonly StoreChecklistItem[]) {
  // Local {id -> checked} state, seeded false for every item defined by the api.
  // Items shipped later show up as unchecked without breaking state.
  //
  // The key is the item's `id`, which is what the api sends. Reading `item_id`
  // here (its name in the SUBMIT payload, not in the catalog) gave every item the
  // same undefined key, so one tick moved every box and nothing could be saved.
  const [state, setState] = useState<Record<number, boolean>>({});
  const knownIds = items.map((i) => i.id).join(',');
  useEffect(() => {
    setState((prev) => {
      const next: Record<number, boolean> = {};
      for (const it of items) next[it.id] = prev[it.id] ?? false;
      return next;
    });
    // knownIds captures the item set — depending on `items` (a new array each
    // render) would loop forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownIds]);
  const setItem = (id: number, checked: boolean) =>
    setState((prev) => ({ ...prev, [id]: checked }));
  // The submit payload calls it `item_id`, so the catalog `id` is mapped across
  // here. Cockpit's phase form does the same thing.
  const entries: ReportChecklistEntry[] = items.map((it) => ({
    item_id: it.id,
    checked: !!state[it.id],
  }));
  const anyChecked = entries.some((e) => e.checked);
  return { state, setItem, entries, anyChecked };
}

function ChecklistBlock({
  items,
  state,
  onChange,
  idPrefix,
}: {
  items: readonly StoreChecklistItem[];
  state: Record<number, boolean>;
  onChange: (item_id: number, checked: boolean) => void;
  idPrefix: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
      <p className="mb-2 text-[13px] font-medium text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Checklist
      </p>
      <div className="space-y-2">
        {items.map((item) => (
          <label key={item.id} className="flex cursor-pointer items-center gap-3 rounded-md">
            <input
              id={`${idPrefix}-${item.id}`}
              type="checkbox"
              checked={!!state[item.id]}
              onChange={(e) => onChange(item.id, e.target.checked)}
              className="h-4 w-4 rounded border-candy-line accent-candy-pink"
            />
            <span className="text-[14px] text-candy-ink dark:text-candy-dark-ink">
              {item.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// --- Photo capture (used by any phase; presign runs after submit) ---

function PhotoField({
  file,
  onFileChange,
  uploading,
  uploaded,
  uploadError,
}: {
  file: File | null;
  onFileChange: (file: File | null) => void;
  uploading: boolean;
  uploaded: boolean;
  uploadError: boolean;
}) {
  // The picker works in its own list shape, the card works in a single file.
  // Wrapping here keeps the swap local: every caller still passes one file.
  // Memoised because the wrapper mints a fresh id each call, and a new id on
  // every render would restart the thumbnail.
  const files = useMemo(() => (file ? [createUploadFile(file)] : []), [file]);

  return (
    <div className="border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
      <p className="mb-2 text-[13px] font-medium text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Storefront photo
      </p>
      {/* One photo per day, so no multi-select. Drag-and-drop, paste, and the
          rear camera come with the shared picker. */}
      <FileUpload
        files={files}
        onChange={(next) => onFileChange(next[0]?.file ?? null)}
        multiple={false}
        maxFiles={1}
        accept={['image/*']}
        capture="environment"
        label="Take photo (optional)"
        disabled={uploading}
      />
      {uploading && (
        <p className="mt-1 flex items-center gap-1 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          <Loader2 size={12} className="animate-spin" aria-hidden /> Uploading photo…
        </p>
      )}
      {uploaded && (
        <p className="mt-1 flex items-center gap-1 text-[12px] text-candy-teal dark:text-candy-teal-dark">
          <CheckCircle2 size={12} aria-hidden /> Photo uploaded.
        </p>
      )}
      {uploadError && (
        <p className="mt-1 text-[12px] text-red-500">
          Photo upload failed — the check-in was still saved. You can retake.
        </p>
      )}
    </div>
  );
}

// --- Rendered summary for a submitted phase ---

function SubmittedSummary({
  report,
  transactionsShown,
}: {
  report: PhaseReport;
  transactionsShown?: boolean;
}) {
  return (
    <InfoBody>
      {report.traffic_count != null && <p>Traffic count: {report.traffic_count}</p>}
      {transactionsShown && report.transactions != null && (
        <p className="mt-1">Transactions: {report.transactions}</p>
      )}
      {report.note && <p className="mt-1">{report.note}</p>}
      {report.photos.length > 0 && <p className="mt-1">Storefront photo uploaded.</p>}
    </InfoBody>
  );
}

// --- Opening phase card ---

function OpeningCard({
  entry,
  checklistItems,
  storeNo,
}: {
  entry: { submitted: boolean; report: PhaseReport | null };
  checklistItems: readonly StoreChecklistItem[];
  storeNo?: number;
}) {
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const checklist = useChecklistState(checklistItems);
  const submit = useSubmitReport(storeNo);
  const uploadPhoto = useUploadReportPhoto(storeNo);

  // No traffic field at open: the store has just unlocked, so the counter
  // reading is always 0 (Steven, 2026-08-10). Closing still captures the day.
  //
  // Submit needs ONE thing done, not every box ticked (Steven, 2026-08-13).
  // Opening carries a checklist and a photo, so those are the two ways in.
  const hasSomething = checklist.anyChecked || pendingPhoto !== null;
  const canSubmit = hasSomething && !submit.isPending && !uploadPhoto.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate(
      {
        phase: 'open',
        ...(checklistItems.length > 0 ? { checklist: checklist.entries } : {}),
      },
      {
        onSuccess: (result) => {
          if (pendingPhoto) {
            uploadPhoto.mutate({ reportId: result.report_id, file: pendingPhoto });
          }
        },
      },
    );
  }

  if (entry.submitted && entry.report) {
    return (
      <CardShell>
        <CardHeader
          icon={<Store size={16} aria-hidden />}
          title="Opening Check-in"
          submittedAt={entry.report.submitted_at}
        />
        <SubmittedSummary report={entry.report} />
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardHeader icon={<Store size={16} aria-hidden />} title="Opening Check-in" />
      <form onSubmit={handleSubmit}>
        <PhotoField
          file={pendingPhoto}
          onFileChange={setPendingPhoto}
          uploading={uploadPhoto.isPending}
          uploaded={uploadPhoto.isSuccess}
          uploadError={uploadPhoto.isError}
        />
        <ChecklistBlock
          items={checklistItems}
          state={checklist.state}
          onChange={checklist.setItem}
          idPrefix="opening-check"
        />
        <SubmitRow
          pending={submit.isPending}
          disabled={!canSubmit}
          label="Submit opening check-in"
          error={submit.isError ? 'Failed to submit. Try again.' : undefined}
        />
      </form>
    </CardShell>
  );
}

// --- Midday phase card ---

function MiddayCard({
  entry,
  checklistItems,
  storeNo,
}: {
  entry: { submitted: boolean; report: PhaseReport | null };
  checklistItems: readonly StoreChecklistItem[];
  storeNo?: number;
}) {
  const [traffic, setTraffic] = useState('');
  const [notes, setNotes] = useState('');
  const checklist = useChecklistState(checklistItems);
  const submit = useSubmitReport(storeNo);

  const trafficNum = traffic === '' ? undefined : Number(traffic);
  const trafficOk = trafficNum === undefined || (Number.isFinite(trafficNum) && trafficNum >= 0);
  // One ticked box or a note is enough (Steven, 2026-08-13). Midday has no photo
  // field. A traffic count on its own does not count, which is the open question
  // recorded on issue #136. A badly formed number still blocks submit.
  const hasSomething = checklist.anyChecked || notes.trim() !== '';
  const canSubmit = trafficOk && hasSomething && !submit.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate({
      phase: 'midday',
      ...(trafficNum !== undefined
        ? { traffic_count: trafficNum, traffic_source: 'manual' }
        : {}),
      ...(notes.trim() ? { note: notes.trim() } : {}),
      ...(checklistItems.length > 0 ? { checklist: checklist.entries } : {}),
    });
  }

  if (entry.submitted && entry.report) {
    return (
      <CardShell>
        <CardHeader
          icon={<Sun size={16} aria-hidden />}
          title="Midday Pulse"
          submittedAt={entry.report.submitted_at}
        />
        <SubmittedSummary report={entry.report} />
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardHeader icon={<Sun size={16} aria-hidden />} title="Midday Pulse" />
      <form onSubmit={handleSubmit}>
        <div className="space-y-3 border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
          <div>
            <FieldLabel htmlFor="midday-traffic">Traffic counter reading</FieldLabel>
            <input
              id="midday-traffic"
              type="number"
              min="0"
              inputMode="numeric"
              value={traffic}
              onChange={(e) => setTraffic(e.target.value)}
              placeholder="0"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel htmlFor="midday-notes">Notes (optional)</FieldLabel>
            <textarea
              id="midday-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything to flag for midday…"
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
        <ChecklistBlock
          items={checklistItems}
          state={checklist.state}
          onChange={checklist.setItem}
          idPrefix="midday-check"
        />
        <SubmitRow
          pending={submit.isPending}
          disabled={!canSubmit}
          label="Submit midday pulse"
          error={submit.isError ? 'Failed to submit. Try again.' : undefined}
        />
      </form>
    </CardShell>
  );
}

// --- Closing phase card ---

function ClosingCard({
  entry,
  checklistItems,
  autoTraffic,
  autoTransactions,
  storeNo,
}: {
  entry: { submitted: boolean; report: PhaseReport | null };
  checklistItems: readonly StoreChecklistItem[];
  autoTraffic: number | null;
  autoTransactions: number | null;
  storeNo?: number;
}) {
  const [traffic, setTraffic] = useState(autoTraffic != null ? String(autoTraffic) : '');
  const [trafficSource, setTrafficSource] = useState<'auto' | 'manual'>(
    autoTraffic != null ? 'auto' : 'manual',
  );
  const [transactions, setTransactions] = useState(
    autoTransactions != null ? String(autoTransactions) : '',
  );
  const [notes, setNotes] = useState('');
  const checklist = useChecklistState(checklistItems);
  const submit = useSubmitReport(storeNo);

  const trafficNum = traffic === '' ? undefined : Number(traffic);
  const trafficOk = trafficNum === undefined || (Number.isFinite(trafficNum) && trafficNum >= 0);
  const txNum = transactions === '' ? undefined : Number(transactions);
  const txOk = txNum === undefined || (Number.isFinite(txNum) && txNum >= 0);
  // Same rule as Midday: one ticked box or a note. Closing has no photo field.
  const hasSomething = checklist.anyChecked || notes.trim() !== '';
  const canSubmit = trafficOk && txOk && hasSomething && !submit.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate({
      phase: 'close',
      ...(trafficNum !== undefined
        ? { traffic_count: trafficNum, traffic_source: trafficSource }
        : {}),
      ...(txNum !== undefined ? { transactions: txNum } : {}),
      ...(notes.trim() ? { note: notes.trim() } : {}),
      ...(checklistItems.length > 0 ? { checklist: checklist.entries } : {}),
    });
  }

  if (entry.submitted && entry.report) {
    return (
      <CardShell>
        <CardHeader
          icon={<Moon size={16} aria-hidden />}
          title="Closing"
          submittedAt={entry.report.submitted_at}
        />
        <SubmittedSummary report={entry.report} transactionsShown />
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardHeader icon={<Moon size={16} aria-hidden />} title="Closing" />
      <form onSubmit={handleSubmit}>
        <div className="space-y-3 border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
          <div>
            <FieldLabel htmlFor="closing-traffic">Final traffic counter reading</FieldLabel>
            <input
              id="closing-traffic"
              type="number"
              min="0"
              inputMode="numeric"
              value={traffic}
              onChange={(e) => {
                setTraffic(e.target.value);
                setTrafficSource('manual');
              }}
              placeholder="0"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel htmlFor="closing-transactions">Transactions</FieldLabel>
            <input
              id="closing-transactions"
              type="number"
              min="0"
              inputMode="numeric"
              value={transactions}
              onChange={(e) => setTransactions(e.target.value)}
              placeholder="0"
              className={inputCls}
            />
          </div>
          <div>
            <FieldLabel htmlFor="closing-notes">Notes (optional)</FieldLabel>
            <textarea
              id="closing-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="End of day notes…"
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
        <ChecklistBlock
          items={checklistItems}
          state={checklist.state}
          onChange={checklist.setItem}
          idPrefix="closing-check"
        />
        <SubmitRow
          pending={submit.isPending}
          disabled={!canSubmit}
          label="Submit closing check-in"
          error={submit.isError ? 'Failed to submit. Try again.' : undefined}
        />
      </form>
    </CardShell>
  );
}

// --- Page shell helpers ---

function PageHead({ label, storeName }: { label: string; storeName?: string }) {
  return (
    <div className="mb-5">
      <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Today</h1>
      <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        {label}
        {storeName && (
          <>
            {' · '}
            <span className="font-medium">{storeName}</span>
          </>
        )}
      </p>
    </div>
  );
}

const EMPTY_ITEMS: readonly StoreChecklistItem[] = [];
const EMPTY_ENTRY = { submitted: false, report: null } as const;

// --- Main export ---

export function Today() {
  const activeStoreNo = useEffectiveStoreNo();

  const meQ = useStoreMe(activeStoreNo);
  const businessDate = todayInStoreZone(meQ.data?.store?.timezone);
  const reportQ = useDailyReport(activeStoreNo, businessDate);

  // Built from `businessDate` (the store's calendar day), not device time, so
  // the header always matches the day the report below is keyed to. Parsed
  // via Date.UTC + timeZone: 'UTC' so the YYYY-MM-DD digits are read as-is,
  // the same technique @ampersandbrands/ui's formatBusinessDate uses.
  const businessDateParts = businessDate.split('-').map(Number);
  const businessYear = businessDateParts[0] ?? 1970;
  const businessMonth = businessDateParts[1] ?? 1;
  const businessDay = businessDateParts[2] ?? 1;
  const todayLabel = new Date(
    Date.UTC(businessYear, businessMonth - 1, businessDay),
  ).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const storeName = meQ.data?.store?.store_name;
  const checklists = meQ.data?.checklist;

  const openItems = useMemo(() => checklists?.open ?? EMPTY_ITEMS, [checklists]);
  const middayItems = useMemo(() => checklists?.midday ?? EMPTY_ITEMS, [checklists]);
  const closeItems = useMemo(() => checklists?.close ?? EMPTY_ITEMS, [checklists]);

  if (meQ.isLoading || reportQ.isLoading) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        <PageHead label={todayLabel} storeName={storeName} />
        <div className="space-y-4">
          {([0, 1, 2] as const).map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      </div>
    );
  }

  const loadErr = reportQ.error ?? meQ.error;
  if (loadErr && !isNotAvailable(loadErr)) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        <PageHead label={todayLabel} storeName={storeName} />
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Could not load today's check-ins. Please try again.
          </p>
        </div>
      </div>
    );
  }

  const phases = reportQ.data?.phases;
  const openEntry = phases?.open ?? EMPTY_ENTRY;
  const middayEntry = phases?.midday ?? EMPTY_ENTRY;
  const closeEntry = phases?.close ?? EMPTY_ENTRY;
  const autoTraffic = reportQ.data?.traffic_auto?.traffic ?? null;
  const autoTransactions = reportQ.data?.traffic_auto?.transactions ?? null;

  // Steven's rule (issue #62): all three phases are ALWAYS independently
  // submittable, no ordering gate. A store that forgot Opening can still
  // submit Midday and Closing.
  const phaseKeys: Array<{ phase: Phase; key: string }> = [
    { phase: 'open', key: `open-${openEntry.report?.report_id ?? 'form'}` },
    { phase: 'midday', key: `midday-${middayEntry.report?.report_id ?? 'form'}` },
    { phase: 'close', key: `close-${closeEntry.report?.report_id ?? 'form'}` },
  ];

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <PageHead label={todayLabel} storeName={storeName} />

      <div className="space-y-4">
        {phaseKeys.map(({ phase, key }) => {
          if (phase === 'open') {
            return (
              <OpeningCard
                key={key}
                entry={openEntry}
                checklistItems={openItems}
                storeNo={activeStoreNo}
              />
            );
          }
          if (phase === 'midday') {
            return (
              <MiddayCard
                key={key}
                entry={middayEntry}
                checklistItems={middayItems}
                storeNo={activeStoreNo}
              />
            );
          }
          return (
            <ClosingCard
              key={key}
              entry={closeEntry}
              checklistItems={closeItems}
              autoTraffic={autoTraffic}
              autoTransactions={autoTransactions}
              storeNo={activeStoreNo}
            />
          );
        })}
      </div>

      <div className="mt-6 flex justify-center">
        <Link
          to="/questions"
          className="flex items-center gap-1.5 text-[13px] text-candy-ink-muted underline-offset-2 hover:text-candy-pink hover:underline dark:text-candy-dark-ink-muted dark:hover:text-candy-pink-dark"
        >
          <MessageCircleQuestion size={14} aria-hidden />
          View store thread
        </Link>
      </div>
    </div>
  );
}
